// Pure aggregation helpers for the Sales RM dashboard. They operate on lead
// records returned by the API (see services/leads.service.ts) - there is no mock
// data here; the seed lives on the server.

import type { RenewalFrequencyValue } from '@/utils/constants';

export type TcStatus = 'Not sent' | 'Not Confirmed' | 'Confirmed';
export type LeadStatusValue = 'Lead' | 'Future Client' | 'Client';
export type PayStatus = 'Not Paid' | 'Partial Payment' | 'Full Payment';
// 'Due' is the promise-to-pay mode: service starts before the client has paid,
// against a date instead of a transaction number (ADR-053). Every "has this
// been paid?" check must keep answering no for it.
export type PaymentMode = 'UPI' | 'Bank Transfer' | 'Razorpay' | 'Due';

/** The mode that stands in for a transaction number. */
export const DUE_PAYMENT_MODE: PaymentMode = 'Due';

/** True when this installment is a promise to pay rather than a payment. */
export const isDueMode = (mode: string | null | undefined): boolean =>
  mode?.trim() === DUE_PAYMENT_MODE;

export interface LeadRecord {
  // NON-ARN client (ADKJ-117): `arn` holds a system-generated 'NA…' code, the
  // ARN lookup is unavailable, and AUM-based products / ADD_ARN cannot be sold.
  isNonArn: boolean;
  id: number;
  leadCode: number;
  arn: string;
  firstName: string;
  middleName: string | null;
  lastName: string | null;
  email: string;
  mobile: string;
  city: string | null;
  state: string | null;
  pincode: string | null;
  // Where the lead came from (plan.md §16.2). Typed as a plain string, not a
  // union of the current options: the column is free text and the stored values
  // are the historical record, so a row written under a label that has since
  // been retired is legitimate data, not a type violation.
  leadSource: string | null;
  leadSourceDetail: string | null;
  // ISO instant, or null. NULL beside a non-null source means it was back-filled
  // rather than captured when the lead was created.
  leadSourceCapturedAt: string | null;
  // Read-only, AMFI-sourced (Fix 1). Written server-side by the ARN lookup and
  // by nothing else, so there is no payload field for either - the leads table
  // and the wizard both render them as facts, never as editable inputs.
  // `kydCompliant: null` means "never fetched", which is NOT the same as `false`
  // ("the registry says this ARN holder is not compliant").
  kydCompliant: boolean | null;
  // 'YYYY-MM-DD', a calendar date - not an ISO instant.
  arnValidTill: string | null;
  // The name employees actually use for the client's firm. Editable, and one of
  // the columns the list searches and sorts by.
  brandName: string | null;
  tcStatus: TcStatus;
  // True when the RM has manually pinned tcStatus (manual override fallback); the
  // server then stops auto-deriving it from the acceptance ledger.
  tcStatusManual?: boolean;
  leadStatus: LeadStatusValue;
  // ─── current-order fields (ADR-048) ─────────────────────────────────────────
  // These describe the client's CURRENT order - the open one, or their most
  // recent if none is open - not the client as a whole. The wizard edits exactly
  // one order at a time.
  orderId?: number | null;
  orderNumber?: number | null;
  orderType?: 'Initial' | 'Add-on' | null;
  paymentStatus: PayStatus;
  paymentAmount: number;
  paymentMode: PaymentMode | null;
  // Company bank account label chosen in Step 3, shown in Step 4 (Fix 5).
  selectedBankLabel: string | null;
  // Payment transaction number/reference entered in Step 4 (Fix 1).
  transactionRef: string | null;
  // Renewal cadence PER PRODUCT (ADR-048), keyed by product code, so one order
  // can mix an annual plan with a quarterly add-on. Optional so older cached
  // rows without the field stay assignable.
  productRenewalFrequencies?: Record<string, RenewalFrequencyValue>;
  // When this client first became a client. Per-order confirmation is
  // `orderOnboardedAt`.
  onboardedAt: string | null;
  orderOnboardedAt?: string | null;
  // Rolled up across EVERY order (ADR-048), so the Clients table shows what the
  // client has paid in total rather than only their latest purchase. Computed
  // server-side per request; absent on the lead-scoped endpoints.
  lifetimePaymentAmount?: number;
  lifetimePaymentStatus?: PayStatus;
  orderCount?: number;
  // Highest wizard step reached (1-5) for the CURRENT order; see
  // lead_orders.stepper / LEAD_STEPPER server-side.
  stepper?: number;
  selectedProducts: string[];
  // Sparse per-product maps from product_details (see ADR-018/019), used to
  // restore Step 2. Keyed by product code.
  customPrices: Record<string, number>;
  productTerms: Record<string, number>;
  productDiscounts: Record<string, ProductDiscount>;
  // Additional ARNs for the ADD_ARN product (see ADR-020), from the relational
  // lead_additional_arns table.
  additionalArns: AdditionalArnInput[];
  /**
   * Per-line override of the multi-year discount on years 2+ (percent). A code
   * absent from the map is priced with the company-wide setting.
   */
  productMultiYearDiscounts: Record<string, number>;
  // Sub-item selections for API bundle products, keyed by product code.
  apiItems?: Record<string, ApiSubItem[]>;
  form: Record<string, string> | null;
  createdAt: string;
}

// A single selectable item within an API bundle product (API_MF_RESEARCH or
// API_CALCULATOR). Mirror of the server-side ApiSubItem in constants/index.ts.
export interface ApiSubItem {
  code: string;
  name: string;
  price: number;
}

// Display helper: joins first + middle + last, dropping blank parts.
export const leadFullName = (
  lead: Pick<LeadRecord, 'firstName' | 'middleName' | 'lastName'>,
): string => [lead.firstName, lead.middleName, lead.lastName].filter(Boolean).join(' ');

export interface AdditionalArnInput {
  arn: string;
  clientName: string;
  /** AUM in crores; summed with primary ARN AUM for MFBOX slab (ADR-021). */
  aum?: number;
  /**
   * Years this ARN is registered for (1-5), chosen per ARN. Undefined reads as
   * 1. The fee multiplies by the years with no discount - it is a flat
   * registration charge, not a subscription.
   */
  termYears?: number;
}

// Per-product discount (ADR-019). `value` is a percentage (0-100) when mode is
// 'percent', otherwise a fixed rupee amount, applied to that product's own line.
export interface ProductDiscount {
  mode: 'amount' | 'percent';
  value: number;
}

// Returned by POST /pricing/calculate-setup when planTier + primaryArnAum are supplied.
export interface SlabInfo {
  id: number;
  rateCard: string;
  band: string;
  setupPrice: number;
  renewalPrice: number;
}

export interface SetupQuoteResult {
  planTier: string | null;
  primaryArnAum: number | null;
  /** Combined primary + additional-ARN AUM used for the MFBOX slab (ADR-021). */
  totalArnAum: number | null;
  termYears: number;
  flatProductSubtotal: number;
  slab: SlabInfo | null;
  approvalRequired: boolean;
  status: 'priced' | 'pending_approval';
  setupAmount: number | null;
  setupYearOneAmount: number | null;
  setupRenewalYearAmount: number | null;
  additionalArnCount: number;
  additionalArnFee: number;
  additionalArnAmount: number;
  // Discount on the AUM/MFBOX slab line only (part of discountAmount).
  setupDiscountAmount: number;
  // Total per-product discount across all lines (flat + AUM), in whole rupees.
  discountAmount: number;
  subtotal: number | null;
  gstRate: number;
  gstAmount: number | null;
  grandTotal: number | null;
  additionalArns: AdditionalArnInput[];
  notes: string[];
  /**
   * Present only on an UPGRADE order. When set, `setupAmount` is the prorated
   * difference between the two tiers rather than a full setup fee, and this is
   * the working behind it - shown in the quotation so the RM can explain the
   * figure to the client.
   */
  upgrade?: UpgradeProration;
}

/** How an upgrade's prorated amount was arrived at. Mirrors the server DTO. */
export interface UpgradeProration {
  fromProductCode: string;
  toProductCode: string;
  /** Which price was differenced: the setup fee, or the renewal. */
  basis: 'setup' | 'renewal';
  /**
   * False when the superseded plan was never activated: nothing has been
   * delivered, so the whole difference is due rather than a part of it.
   */
  prorated: boolean;
  aumCrores: number;
  fromPrice: number;
  toPrice: number;
  difference: number;
  monthsRemaining: number;
  periodMonths: number;
  amount: number;
  /** The renewal date the upgraded plan keeps; null if it never had one. */
  inheritedDueDate: string | null;
  approvalRequired: boolean;
}

export interface ProductInfo {
  code: string;
  name: string;
  price: number;
  isAumBased?: boolean;
  rateCard?: string | null;
  noDiscount?: boolean;
  /**
   * Catalogue default for the years-2+ multi-year discount (ADKJ-123), null =
   * follow the company-wide setting. Used as the fallback shown/applied by
   * MultiYearTermDiscountControl before the RM overrides it for this sale.
   */
  multiYearDiscountPercent?: number | null;
}

export interface DashRange {
  from: Date;
  to: Date;
  label: string;
}

export const MONTHLY_TARGET = 1_200_000;
export const YEARLY_TARGET = 12_000_000;

const pad = (n: number): string => String(n).padStart(2, '0');

// maximumFractionDigits stays 0 for whole rupees (no forced ".00"), but a
// fractional amount (ADKJ-124, e.g. "Website (AI based)" at 3093.2203/year)
// still needs to show real paise - so the cap is only raised when there's
// something after the decimal point to show.
export const inr = (n: number): string =>
  n.toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: Number.isInteger(n) ? 0 : 2,
  });

export const presetRange = (kind: 'month' | 'year' | 'all'): DashRange => {
  const now = new Date();
  if (kind === 'month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { from, to, label: from.toLocaleString('en-IN', { month: 'long', year: 'numeric' }) };
  }
  if (kind === 'all') {
    // Everything through today. Floor at the epoch so no real record can fall
    // below it. Matches the Leads/Clients tables, which apply no date filter by
    // default, so the dashboard shows the same data on first load instead of
    // hiding leads whose created_at falls outside the current month.
    return { from: new Date(0), to: now, label: 'All Time' };
  }
  // 'year' = calendar year to date (1 Jan -> today), i.e. YTD.
  const from = new Date(now.getFullYear(), 0, 1);
  return { from, to: now, label: `This Year (${now.getFullYear()})` };
};

// Financial year to date: the most recent FY start (admin-configured month/day)
// that is on or before today, through today. Defaults to 1 Apr if unset.
export const fytdRange = (startMonth: number, startDay: number): DashRange => {
  const now = new Date();
  // Clamp the day to the month's length so an out-of-range value (e.g. 31 for
  // April) can't silently roll the date into the next month.
  const daysInMonth = new Date(2001, startMonth, 0).getDate();
  const day = Math.min(Math.max(1, startDay), daysInMonth);
  let from = new Date(now.getFullYear(), startMonth - 1, day);
  if (from.getTime() > now.getTime()) {
    from = new Date(now.getFullYear() - 1, startMonth - 1, day);
  }
  const startYear = from.getFullYear();
  // "FY 2026-27" when the year spans two calendar years (start after January),
  // otherwise the single start year.
  const label =
    startMonth > 1
      ? `FYTD ${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`
      : `FYTD ${startYear}`;
  return { from, to: now, label };
};

export const filterByRange = (rows: LeadRecord[], range: DashRange): LeadRecord[] => {
  const fromT = range.from.getTime();
  const toT = range.to.getTime() + 86_400_000 - 1;
  return rows.filter((r) => {
    const t = new Date(r.createdAt).getTime();
    return t >= fromT && t <= toT;
  });
};

export const countByKey = <T extends string>(
  rows: LeadRecord[],
  key: (r: LeadRecord) => T,
): Record<T, number> => {
  const out = {} as Record<T, number>;
  rows.forEach((r) => {
    const k = key(r);
    out[k] = (out[k] ?? 0) + 1;
  });
  return out;
};

export interface ProductAggregate {
  code: string;
  name: string;
  units: number;
  revenue: number;
}

export const aggregateProducts = (
  rows: LeadRecord[],
  catalog: ProductInfo[],
): ProductAggregate[] => {
  const nameByCode = new Map(catalog.map((p) => [p.code, p.name]));
  const map = new Map<string, ProductAggregate>();
  rows.forEach((r) => {
    const share = r.selectedProducts.length ? r.paymentAmount / r.selectedProducts.length : 0;
    r.selectedProducts.forEach((code) => {
      const name = nameByCode.get(code);
      if (!name) return;
      const prev = map.get(code) ?? {
        code,
        name,
        units: 0,
        revenue: 0,
      };
      prev.units += 1;
      prev.revenue += share;
      map.set(code, prev);
    });
  });
  return Array.from(map.values()).sort((a, b) => b.units - a.units);
};

export interface CityMetrics {
  city: string;
  state: string;
  leads: number;
  clients: number;
  products: number;
  revenue: number;
}

export const aggregateByCity = (rows: LeadRecord[]): CityMetrics[] => {
  const map = new Map<string, CityMetrics>();
  rows.forEach((r) => {
    const city = r.city ?? 'Unknown';
    const state = r.state ?? 'Unknown';
    const key = `${city}|${state}`;
    const prev = map.get(key) ?? {
      city,
      state,
      leads: 0,
      clients: 0,
      products: 0,
      revenue: 0,
    };
    prev.leads += 1;
    if (r.leadStatus === 'Client') prev.clients += 1;
    prev.products += r.selectedProducts.length;
    prev.revenue += r.paymentAmount;
    map.set(key, prev);
  });
  return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
};

export interface StateMetrics {
  state: string;
  leads: number;
  clients: number;
  products: number;
  revenue: number;
}

export const aggregateByStateFull = (rows: LeadRecord[]): StateMetrics[] => {
  const map = new Map<string, StateMetrics>();
  rows.forEach((r) => {
    const state = r.state ?? 'Unknown';
    const prev = map.get(state) ?? {
      state,
      leads: 0,
      clients: 0,
      products: 0,
      revenue: 0,
    };
    prev.leads += 1;
    if (r.leadStatus === 'Client') prev.clients += 1;
    prev.products += r.selectedProducts.length;
    prev.revenue += r.paymentAmount;
    map.set(state, prev);
  });
  return Array.from(map.values()).sort((a, b) => b.leads - a.leads);
};

export interface TimePoint {
  label: string;
  leads: number;
  clients: number;
  revenue: number;
  sortKey: string;
}

/** Revenue + leads grouped by day (ranges <= 2 months) or month. */
export const timeSeries = (rows: LeadRecord[], range: DashRange): TimePoint[] => {
  const months = Math.max(
    1,
    Math.round((range.to.getTime() - range.from.getTime()) / (30 * 86_400_000)),
  );
  const bucketByDay = months <= 2;
  const map = new Map<string, TimePoint>();
  rows.forEach((r) => {
    const d = new Date(r.createdAt);
    const key = bucketByDay
      ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      : `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
    const label = bucketByDay
      ? d.toLocaleString('en-IN', { day: '2-digit', month: 'short' })
      : d.toLocaleString('en-IN', { month: 'short', year: '2-digit' });
    const prev = map.get(key) ?? {
      label,
      leads: 0,
      clients: 0,
      revenue: 0,
      sortKey: key,
    };
    prev.leads += 1;
    if (r.leadStatus === 'Client') prev.clients += 1;
    prev.revenue += r.paymentAmount;
    map.set(key, prev);
  });
  return Array.from(map.values()).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
};

export interface PendingAction {
  id: number;
  code: number;
  name: string;
  reason: string;
  ageDays: number;
}

// Surfaces the next workflow step still pending for each lead, so the RM sees
// what to do next (send the T&C link, follow up, complete the Billing Handover,
// collect payment). Once the lead accepts the T&C, the Billing Handover (Step 4)
// becomes the next action and shows immediately.
export const pendingActions = (rows: LeadRecord[]): PendingAction[] => {
  const now = Date.now();
  const out: PendingAction[] = [];
  rows.forEach((r) => {
    const ageDays = Math.floor((now - new Date(r.createdAt).getTime()) / 86_400_000);
    const base = { id: r.id, code: r.leadCode, name: leadFullName(r) };
    // Onboarding completion is signalled by onboardedAt, not leadStatus: an RM can
    // mark a lead 'Client' in Step 3 before the Billing Handover is done, and that
    // lead must still surface the handover task.
    const isOnboarded = Boolean(r.onboardedAt);

    // Not yet onboarded: the next action follows the T&C acceptance funnel.
    if (!isOnboarded) {
      if (r.tcStatus === 'Confirmed') {
        // T&C accepted -> the next step is the Billing Handover (Step 4).
        out.push({ ...base, reason: 'Complete the Billing Handover - T&C accepted', ageDays });
      } else if (r.tcStatus === 'Not Confirmed' && ageDays >= 5) {
        // Link sent, waiting on the lead to accept.
        out.push({ ...base, reason: 'Follow up on T&C confirmation', ageDays });
      } else if (r.tcStatus === 'Not sent' && ageDays >= 2) {
        // No confirmation email / T&C link sent yet.
        out.push({ ...base, reason: 'Send the confirmation email & T&C link', ageDays });
      }
      return;
    }

    // Onboarded but the payment is still outstanding.
    if (r.paymentStatus === 'Partial Payment' && ageDays >= 7) {
      out.push({ ...base, reason: 'Collect remaining payment', ageDays });
    } else if (r.paymentStatus === 'Not Paid') {
      out.push({ ...base, reason: 'Collect payment for onboarded client', ageDays });
    }
  });
  return out.sort((a, b) => b.ageDays - a.ageDays).slice(0, 12);
};

/**
 * The AMFI columns, rendered as facts rather than as values someone typed.
 *
 * Shared by the leads list and the orders/clients list, which show the same two
 * columns - an onboarded client leaves the leads list, so both views need them
 * and they must read identically in each.
 *
 * `kydCompliant` is THREE-valued and each state reads differently: `true` /
 * `false` are the registry's answer, and `null` is "we have never fetched it".
 * Rendering null as "No" would put an unverified claim about a distributor's
 * compliance on screen, so it says exactly what is true instead.
 */
export const kydLabel = (value: boolean | null): string => {
  if (value === null) return 'Not fetched';
  return value ? 'Compliant' : 'Not compliant';
};

export const kydTone = (value: boolean | null): 'success' | 'warning' | 'neutral' => {
  if (value === null) return 'neutral';
  return value ? 'success' : 'warning';
};

/**
 * 'YYYY-MM-DD' -> '12 Mar 2027'.
 *
 * Parsed field-by-field rather than through `new Date(iso)`, which reads a bare
 * date as UTC midnight and therefore renders as the PREVIOUS day for anyone west
 * of Greenwich. An unrecognised string is returned untouched rather than shown
 * as "Invalid Date".
 */
export const formatValidTill = (iso: string | null): string => {
  if (!iso) return '-';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};
